import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";
import { useState } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const MirrorTypeCard = ({ title, description, image }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="group overflow-hidden"
  >
    <div className="h-64 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
    </div>
    <div className="bg-[#F9F9F7] p-6">
      <h3 className="font-heading text-2xl font-medium text-[#1A202C] mb-2">{title}</h3>
      <p className="font-body text-[#4B5563]">{description}</p>
    </div>
  </motion.div>
);

const CustomMirrorsPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="pt-20">
      <SEO
        title="Custom Mirrors"
        description="Custom mirrors for bathrooms, vanities, gyms, and closets in coastal NC. Brunswick Glass fabricates mirrors to exact dimensions with polished or beveled edges. Professional installation."
        keywords="custom mirrors, bathroom mirror, vanity mirror, gym mirrors, closet mirror, wall mirror NC, Brunswick County mirror installation"
        ogImage="https://images.unsplash.com/photo-1763485957127-5645e9348426?w=1200&q=80"
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#F9F9F7]">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1763485957127-5645e9348426?w=1920&q=80"
            alt="Custom mirrors"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="section-container relative z-10 w-full py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0F4C81] font-body mb-6 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4"
            >
              Residential Services
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6"
            >
              Custom Mirrors
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-xl leading-relaxed text-[#4B5563] mb-8"
            >
              Custom mirrors add light, depth, and functionality to any space. We fabricate mirrors to exact dimensions for bathrooms, vanities, gyms, closets, and commercial interiors.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A355C] text-white rounded-full px-8 h-14 font-body font-semibold">
                    Get a Free Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] p-8">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onClose={() => setQuoteOpen(false)} />
                </DialogContent>
              </Dialog>
              <a href="tel:910-457-4757">
                <Button variant="outline" className="border-2 border-[#1A202C] rounded-full px-8 h-14 font-body font-semibold">
                  <Phone className="w-5 h-5 mr-2" /> 910-457-4757
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features - Reduced spacing */}
      <section className="py-8 bg-white">
        <div className="section-container">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 bg-[#F9F9F7] p-4">
              <Check className="w-5 h-5 text-[#0F4C81]" />
              <span className="font-body text-[#1A202C]">Custom-cut to exact dimensions</span>
            </div>
            <div className="flex items-center gap-3 bg-[#F9F9F7] p-4">
              <Check className="w-5 h-5 text-[#0F4C81]" />
              <span className="font-body text-[#1A202C]">Polished or beveled edges</span>
            </div>
            <div className="flex items-center gap-3 bg-[#F9F9F7] p-4">
              <Check className="w-5 h-5 text-[#0F4C81]" />
              <span className="font-body text-[#1A202C]">Professional installation</span>
            </div>
            <div className="flex items-center gap-3 bg-[#F9F9F7] p-4">
              <Check className="w-5 h-5 text-[#0F4C81]" />
              <span className="font-body text-[#1A202C]">Clean lines and perfect fit</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mirror Types - Reduced top spacing */}
      <section className="py-16 md:py-20 bg-white">
        <div className="section-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-12"
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">
              Mirror Solutions for Every Space
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl">
              From bathroom vanities to full gym installations, we create mirrors that enhance any environment.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <MirrorTypeCard
              title="Bathroom & Vanity Mirrors"
              description="Custom-sized mirrors that perfectly fit your bathroom space, enhancing light and creating a sense of openness."
              image="https://images.unsplash.com/photo-1763485957127-5645e9348426?w=800&q=80"
            />
            <MirrorTypeCard
              title="Wall Mirrors"
              description="Large format mirrors for living rooms, entryways, and hallways that add depth and elegance to any space."
              image="https://images.unsplash.com/photo-1711906452433-dc5f0925ff04?w=800&q=80"
            />
            <MirrorTypeCard
              title="Gym Mirrors"
              description="Durable, large-scale mirrors designed for home gyms and fitness spaces with proper mounting for safety."
              image="https://images.pexels.com/photos/917660/pexels-photo-917660.jpeg?w=800"
            />
            <MirrorTypeCard
              title="Closet Mirrors"
              description="Full-length mirrors for walk-in closets and dressing areas, maximizing functionality and style."
              image="https://images.unsplash.com/photo-1765766600589-ddad380d6534?w=800&q=80"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl font-medium text-white mb-4">Add Light & Depth to Your Space</h2>
          <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let us create the perfect custom mirror for your home or business. Contact us for a free consultation.
          </p>
          <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-[#0F4C81] hover:bg-white/90 rounded-full px-8 h-14 font-body font-semibold">
                Get Your Free Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-8">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
              </DialogHeader>
              <QuoteForm onClose={() => setQuoteOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default CustomMirrorsPage;
